import { ReleaseInfo as ReleaseInfoData } from '@/generated/ReleaseInfo';
import { _isNil, _noop, _notNil } from '@/littledash';
import { RouteService } from '@/support/RouteService';
import { revalidateDocument } from '@/utils/ReleaseUpdate.utils';
import { formatDistanceToNow } from 'date-fns';
import { debounceTime, fromEvent, merge, type Subscription } from 'rxjs';
import { DateUtils } from './Date.utils';
import DeviceEventBus from '@/utils/devices/DeviceEventBus';

export interface ReleaseCheckerNoUpdate {
  type: 'no-update';
}

export interface ReleaseCheckerUpdateAvailable {
  type: 'update-available';
  time: number;
  commit: string;
  version: string;
}

export type ReleaseCheckerCheckResponse = ReleaseCheckerNoUpdate | ReleaseCheckerUpdateAvailable;

export interface ReleaseInfo {
  releaseVersion: string;
  commitId: string;
  commitTime: string;
}

interface ReleaseCheckerProps {
  onUpdateAvailable: (release: Omit<ReleaseCheckerUpdateAvailable, 'type'>) => void;
}

export class ReleaseChecker {
  static #defaultTimeout = 1000 * 60 * 15; // 15 mins
  static #timeoutAfterInitialise = 1000 * 30; // 30 seconds
  static #eventEmitter = new EventTarget();
  #timeoutID: ReturnType<typeof setTimeout> | null = null;
  #eventSub$?: Subscription;
  #revalidatedForCommit: string | null = null;
  #destroyed = false;

  static async fetchLatestReleaseInfo(): Promise<ReleaseInfo> {
    try {
      return fetch(`/release-info.json?v=${Date.now()}`)
        .then((response) => {
          if (response.ok) {
            return response.text();
          }
          return Promise.reject(new Error('Could not fetch latest release info', { cause: response.statusText }));
        })
        .then((text) => {
          const detail = JSON.parse(text);
          ReleaseChecker.#eventEmitter.dispatchEvent(new CustomEvent<ReleaseInfo>('release-check:result', { detail }));
          return detail;
        });
    } catch (e) {
      return Promise.reject(new Error('Could not fetch latest release info', { cause: e }));
    }
  }

  static subscribe(listener: (event: CustomEvent<ReleaseInfo>) => void, signal: AbortSignal) {
    ReleaseChecker.#eventEmitter.addEventListener('release-check:result', listener as EventListener, {
      passive: true,
      signal,
    });
  }

  private props: ReleaseCheckerProps;

  constructor(props: ReleaseCheckerProps) {
    this.props = props;
  }

  initialise(): ReleaseChecker {
    window['InVivo'] = {
      debug: () => this.log(),
      enableFeatures: (...flags: Array<string>) =>
        this.updateFeatureFlags(
          flags.map((name) => ({
            name,
            value: true,
          }))
        ),
      disableFeatures: (...flags: Array<string>) =>
        this.updateFeatureFlags(
          flags.map((name) => ({
            name,
            value: false,
          }))
        ),
      device_event_bus: DeviceEventBus,
    };
    this.#eventSub$ = merge(
      fromEvent(document, 'visibilitychange', { passive: true }),
      fromEvent(window, 'online', { passive: true }),
      fromEvent(window, 'offline', { passive: true })
    )
      .pipe(debounceTime(1000))
      .subscribe(() => {
        if (navigator.onLine && document.visibilityState === 'visible') {
          this.#scheduleCheck({ timeout: this.#initialCheckTimeout, force: true });
        } else {
          this.#clearSchedule();
        }
      });
    this.#scheduleCheck({ timeout: this.#initialCheckTimeout });
    return this;
  }

  destroy() {
    this.#destroyed = true;
    this.#eventSub$?.unsubscribe();
    this.#clearSchedule();
    delete window['InVivo'];
  }

  get #nextCheckTime(): number {
    const value = Number(localStorage.getItem('release-check.next') ?? '0');
    return Number.isFinite(value) ? value : 0;
  }

  set #nextCheckTime(value: number) {
    localStorage.setItem('release-check.next', `${value}`);
  }

  /**
   * How long to wait before the first check of this page load, or of a tab the user has just
   * returned to. The persisted deadline throttles rapid reloads, but it must never postpone
   * these moments — they are exactly when a stale bundle surfaces — so it is capped. The
   * deadline is written before the check runs and is shared across tabs, so an inherited
   * value can otherwise be up to a full interval in the future.
   */
  get #initialCheckTimeout(): number {
    const remaining = this.#nextCheckTime - Date.now();
    if (remaining <= 0) {
      return ReleaseChecker.#timeoutAfterInitialise;
    }
    return Math.min(remaining, ReleaseChecker.#timeoutAfterInitialise);
  }

  #clearSchedule() {
    if (_notNil(this.#timeoutID)) {
      clearTimeout(this.#timeoutID);
      this.#timeoutID = null;
    }
  }

  /**
   * A check that is already awaiting the network has no timer to clear, so a `destroy()`
   * landing during that await cannot stop the re-arm that follows it — both `checkNow()` and
   * the polling cadence re-arm from a `finally`. Without this guard that re-armed timer is
   * unreachable by any later cleanup and keeps rescheduling itself forever.
   */
  #scheduleCheck(props: { force?: boolean; timeout?: number; reschedule?: boolean } = {}) {
    if (this.#destroyed) {
      return;
    }
    if (navigator.onLine && document.visibilityState === 'visible') {
      const force = props?.force ?? false;
      const reschedule = props?.reschedule ?? true;
      const timeout = props?.timeout ?? ReleaseChecker.#defaultTimeout;
      if (_isNil(this.#timeoutID) || force) {
        this.#clearSchedule();
        this.#nextCheckTime = Date.now() + timeout;
        this.#timeoutID = setTimeout(() => {
          this.#timeoutID = null;
          this.check()
            .then((result) => {
              if (result.type === 'update-available' && !this.#destroyed) {
                this.props.onUpdateAvailable({ commit: result.commit, version: result.version, time: result.time });
              }
            })
            .catch(_noop)
            .finally(() => {
              if (reschedule) {
                this.#scheduleCheck();
              }
            });
        }, timeout);
      }
    }
  }

  async check(): Promise<ReleaseCheckerCheckResponse> {
    try {
      const {
        releaseVersion: latestReleaseVersion,
        commitTime: latestReleaseCommitTime,
        commitId: latestReleaseCommitId,
      } = await ReleaseChecker.fetchLatestReleaseInfo();
      if (_notNil(latestReleaseCommitId) && ReleaseInfoData.commitId !== latestReleaseCommitId) {
        const time = Date.parse(latestReleaseCommitTime);
        this.#revalidateForRelease(latestReleaseCommitId);
        return { type: 'update-available', commit: latestReleaseCommitId, version: latestReleaseVersion, time };
      }
      return { type: 'no-update' };
    } catch (e) {
      return Promise.reject(new Error('Release Check Failed', { cause: e }));
    }
  }

  /**
   * Pulls the new index.html into the HTTP cache the moment an update is detected, so any
   * later document load — the update toast, a manual refresh, a browser session restore, the
   * chunk-load recovery — boots the new release instead of replaying the document that went
   * stale. This cannot rescue the running page, whose bundle still asks for the old chunk
   * names. Once per detected release: the poll re-reports the same update every interval and
   * re-fetching each time would be pointless traffic.
   */
  #revalidateForRelease(commitId: string): void {
    if (this.#revalidatedForCommit === commitId) {
      return;
    }
    this.#revalidatedForCommit = commitId;
    revalidateDocument().catch(_noop);
  }

  /**
   * Runs a release check immediately, outside the polling schedule, and resolves with the
   * result so the caller decides how to react. Deliberately not gated on `navigator.onLine`
   * or `document.visibilityState` the way the polling schedule is: the only caller is a
   * just-completed authentication handshake, which proves connectivity far better than
   * `navigator.onLine` does, and a backgrounded tab redeeming an SSO short-lived token is
   * precisely the case that must not be skipped. The regular cadence is re-armed afterwards
   * so the schedule is never lost.
   */
  async checkNow(): Promise<ReleaseCheckerCheckResponse> {
    this.#clearSchedule();
    try {
      return await this.check();
    } finally {
      this.#scheduleCheck();
    }
  }

  async log() {
    const checkResult = await this.check();
    /* eslint-disable no-console */
    switch (checkResult.type) {
      case 'no-update': {
        console.log(
          '%c 🟢 Latest version 🟢 ',
          'background-color: green; color: white; font-style: bold; border: 5px solid limegreen; font-size: 5em;'
        );
        const releaseData = new Date(ReleaseInfoData.commitTime);
        console.table({
          Version: ReleaseInfoData.releaseVersion,
          Commit: ReleaseInfoData.commitId,
          'Released On':
            DateUtils.renderDateTime(ReleaseInfoData.commitTime) +
            ' (' +
            formatDistanceToNow(releaseData, { includeSeconds: true, addSuffix: true }) +
            ')',
        });
        break;
      }
      case 'update-available': {
        console.log(
          '%c 🚫 Update available 🚫 ',
          'background-color: yellow; color: red; font-style: bold; border: 5px solid red; font-size: 5em;'
        );
        const releaseDate = new Date(checkResult.time);
        console.table({
          Version: checkResult.version,
          Commit: checkResult.commit,
          'Released On':
            DateUtils.renderDateTime(releaseDate.toISOString()) +
            ' (' +
            formatDistanceToNow(releaseDate, { includeSeconds: true, addSuffix: true }) +
            ')',
        });
        break;
      }
    }
    /* eslint-enable no-console */
  }

  private async updateFeatureFlags(flags: Array<{ name: string; value: boolean }>) {
    const { method, url } = RouteService.api({
      endpoint: 'PATCH /api/v1/team/feature-flags',
      path: undefined,
    });
    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
        'Team-Id': localStorage.getItem('team_id') ?? '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(flags),
    });
    /* eslint-disable no-console */
    if (response.ok) {
      console.log(
        `%c Updated ${flags.length} flag(s) `,
        'font-style: bold; font-size: 2em; background-color: green; color: white; padding: 5px;'
      );
    } else {
      console.log(
        `%c Update failed for ${flags.length} flag(s) `,
        'background-color: red; color: yellow; font-style: bold; font-size: 2em; padding: 5px;'
      );
    }
    /* eslint-enable no-console */
  }
}
